// Copyright (c) 2021, Picsa Advisory Services (Pty) Ltd. All rights reserved.

import React from 'react'
import { withRouter } from 'react-router-dom'
import PropTypes from 'prop-types'
import moment from 'moment'
import { graphql, compose } from 'react-apollo'
import gql from 'graphql-tag'

import {
  Table,
  PageNavigationButtons,
  Spinner,
  SvgIcon,
  PayrollCycleName,
  SpanWithData,
  Warning,
  HoverCard,
  PeekDeduction,
  Button
} from '../'

import {
  showAsCurrency,
  PageNavigationControl,
  getPageCursor,
  saveCursor
} from '../../utils'


export class InvestmentProductPayments extends React.PureComponent {
  static propTypes = {
    clientInvestmentPolicies: PropTypes.arrayOf(PropTypes.string).isRequired,
    contentTypeModel: PropTypes.string.isRequired,
    resultsPerPage: PropTypes.number,
    // TODO: specify which fetched fields to match to corresponding column names to be displayed instead of default
    // columns: PropTypes.arrayOf(PropTypes.shape({
    //   field: PropTypes.string.isRequired,
    //   columnName: PropTypes.string.isRequired
    // })),
    className: PropTypes.string
  }

  static defaultProps = {
    resultsPerPage: 15,
    className: ''
  }

  state = {
    refetch: false,
    pageNav: new PageNavigationControl()
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.error && !this.state.refetch) {
      this.setState({
        refetch: true
      })
      nextProps.refetch()
    }

    if (this.state.refetch) {
      this.setState({
        refetch: false
      })
    }

    if (!nextProps.loading && nextProps.paymentrelations) {
      let paymentListPageState = JSON.parse(sessionStorage.getItem('paymentListPageState'))
      if (paymentListPageState) {
        this.state.pageNav.setPageStates(null, null, paymentListPageState)
        sessionStorage.removeItem('paymentListPageState')
        this.props.refetch({ cursor: paymentListPageState.cursor })
      } else {
        this.state.pageNav.setPageStates(this.props.paymentrelations, nextProps.paymentrelations)
      }
    }
  }

  savePaginationState = (startCursor) => {
    sessionStorage.setItem('paymentListPageState', JSON.stringify({
      firstPage: this.state.pageNav.firstPage,
      lastPage: this.state.pageNav.lastPage,
      currentPage: this.state.pageNav.currentPage,
      pages: this.state.pageNav.pages,
      cursor: saveCursor(startCursor)
    }))
  }

  saveStateAndGoToDetailPage = (payrollCycleId, startCursor) => {
    this.savePaginationState(startCursor)
    this.props.history.push(`/os/operations/general/payrollruns/${payrollCycleId}`)
  }

  render() {
    if (this.props.loading) {
      return <Spinner type='tail' />
    }

    if (this.props.error) {
      return (
        <Warning className={this.props.className ? this.props.className : ''}>
          Error loading data.
        </Warning>
      )
    }

    let paymentRelations = this.props.paymentrelations

    if (paymentRelations.edges.length === 0) {
      return (
        <div className={`slds-m-left_large ${this.props.className}`}>
          <SvgIcon
            variant='utility'
            symbol='info'
            colour='light'
            size='small'
            className='slds-m-right_medium'
          />
          No payments records to display.
        </div>
      )
    }

    let currentPageData = paymentRelations.edges.map((paymentrelation, index) => {
      let payment = paymentrelation.node.payment
      return (
        <tr
          className={`${index === 0 ? 'slds-border_top' : ''}`}
          key={`${payment.id}_payment_${index}`}>
          <td data-label='Payroll date'>
            {payment.workflowrunDeducting && payment.workflowrunDeducting.payrollrunDeducted
              ? moment(payment.workflowrunDeducting.payrollrunDeducted.paidOn).format('DD MMM YYYY')
              : '-'
            }
          </td>
          <td data-label='Type'>
            <SpanWithData forField='PaymentPaymentType' searchKey={payment.paymentType} />
          </td>
          <td data-label='Source'>
            {payment.source === 'CLIENT'
              ? 'Self'
              : `${payment.source.charAt(0)}${payment.source.slice(1).toLowerCase()}`
            }
          </td>
          <td data-label='Status'>
            <SpanWithData forField='PaymentStatus' searchKey={payment.status} />
          </td>
          <td data-label='State'>
            <SpanWithData forField='PaymentState' searchKey={payment.state} />
          </td>
          <td data-label='Deduction'>
            {payment.workflowrunDeducting && payment.workflowrunDeducting.payrollrunDeducted
              ? <HoverCard width={670} height={245} content={(ref, coords) => (
                  <PeekDeduction ref={ref} coords={coords} deduction={payment.workflowrunDeducting.id}/>
                )}>
                  <Button type='reset' onClick={() => (
                    this.saveStateAndGoToDetailPage(payment.workflowrunDeducting.payrollrunDeducted.id, startCursor)
                  )}>
                    <PayrollCycleName
                      payrollCycleId={payment.workflowrunDeducting.payrollrunDeducted.payrollcycle.id}
                    />
                  </Button>
                </HoverCard>
              : '-'
            }
          </td>
          <td data-label='Receipting'>
            {payment.workflowrunReceipting && payment.workflowrunReceipting.payrollrunReceipted
              ? <Button type='reset' onClick={() => (
                    this.saveStateAndGoToDetailPage(payment.workflowrunReceipting.payrollrunReceipted.id, startCursor)
                  )}>
                  <PayrollCycleName
                    payrollCycleId={payment.workflowrunReceipting.payrollrunReceipted.payrollcycle.id}
                  />
                </Button>
              : 'None'
            }
          </td>
          <td data-label='Amount'>
            {showAsCurrency('R', payment.amount, 2)}
          </td>
          <td data-label='Original amount'>
            {showAsCurrency('R', payment.originalAmount, 2)}
          </td>
        </tr>
      )
    })

    let pageNav = this.state.pageNav
    let startCursor = paymentRelations.pageInfo.startCursor
    let totalCount = paymentRelations.totalCount

    return (
      <div className={this.props.className ? this.props.className : ''}>
        <Table
          columns={['Payroll date', 'Type', 'Source', 'Status', 'State', 'Deduction', 'Receipting', 'Amount', 'Original amount']}
          rows={currentPageData}
          objectRows={true}
          noRowBorders={true}
          className="slds-max-medium-table_stacked-horizontal slds-no-row-hover"
        />
        <PageNavigationButtons
          onNextClick={() => pageNav.nextPage(this.props.fetchNext, this.props.resultsPerPage)}
          onPreviousClick={() => pageNav.previousPage(this.props.fetchPrevious, this.props.resultsPerPage)}
          previousDisabled={pageNav.firstPage}
          nextDisabled={pageNav.lastPage}
          startCursor={startCursor}
          totalCount={totalCount}
          resultsPerPage={this.props.resultsPerPage}
          resultsText='payments'
          onPageNumberClick={pageNumber => this.props.refetch({ cursor: getPageCursor(pageNumber, this.props.resultsPerPage) })}
          className="slds-p-around_medium"
        />
      </div>
    )
  }
}

const getPaymentRelations = gql`
  query getPaymentRelations($clientInvestmentPolicies: [String], $contentTypeModel: String!, $resultsPerPage: Int!, $cursor: String) {
    paymentrelations(objectIds: $clientInvestmentPolicies, contentType_Model: $contentTypeModel, orderBy: "-payment__workflowrun_deducting__payrollrun_deducted__paid_on", first: $resultsPerPage, after: $cursor) {
      totalCount
      edges {
        node {
          id
          payment {
            id
            amount
            originalAmount
            state
            status
            source
            paymentType
            workflowrunDeducting {
              id
              status
              payrollrunDeducted {
                id
                paidOn
                payrollcycle {
                  id
                }
              }
            }
            workflowrunReceipting {
              id
              payrollrunReceipted {
                id
                payrollcycle {
                  id
                }
              }
            }
            transactedIn {
              id
            }
          }
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`

const getPreviousPaymentRelations = gql`
  query getPaymentRelations($clientInvestmentPolicies: [String], $contentTypeModel: String!, $resultsPerPage: Int!, $cursor: String) {
    paymentrelations(objectIds: $clientInvestmentPolicies, contentType_Model: $contentTypeModel, orderBy: "-payment__workflowrun_deducting__payrollrun_deducted__paid_on", last: $resultsPerPage, before: $cursor) {
      totalCount
      edges {
        node {
          id
          payment {
            id
            amount
            originalAmount
            state
            status
            source
            paymentType
            workflowrunDeducting {
              id
              status
              payrollrunDeducted {
                id
                paidOn
                payrollcycle {
                  id
                }
              }
            }
            workflowrunReceipting {
              id
              payrollrunReceipted {
                id
                payrollcycle {
                  id
                }
              }
            }
            transactedIn {
              id
            }
          }
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`

const withData =
  graphql(getPaymentRelations, {
    options: ({ clientInvestmentPolicies, contentTypeModel, resultsPerPage = 15, cursor }) => ({
      variables: {
        clientInvestmentPolicies,
        contentTypeModel,
        resultsPerPage,
        cursor
      },
      fetchPolicy: 'network-only'
    }),
    props: ({ ownProps: { clientInvestmentPolicies, contentTypeModel }, data: { loading, error, refetch, paymentrelations, fetchMore } }) => {
      return {
        loading,
        error,
        refetch,
        paymentrelations,
        fetchNext: (resultsPerPage) => {
          return fetchMore({
            query: getPaymentRelations,
            variables: {
              clientInvestmentPolicies,
              contentTypeModel,
              resultsPerPage,
              cursor: paymentrelations.pageInfo.endCursor
            },
            updateQuery: (previousResult, { fetchMoreResult }) => {
              const newEdges = fetchMoreResult.paymentrelations.edges
              const pageInfo = fetchMoreResult.paymentrelations.pageInfo
              return {
                paymentrelations: {
                  __typename: previousResult.paymentrelations.__typename,
                  edges: [...newEdges],
                  pageInfo
                }
              }
            }
          })
        },
        fetchPrevious: (resultsPerPage) => {
          return fetchMore({
            query: getPreviousPaymentRelations,
            variables: {
              clientInvestmentPolicies,
              contentTypeModel,
              resultsPerPage,
              cursor: paymentrelations.pageInfo.startCursor
            },
            updateQuery: (previousResult, { fetchMoreResult }) => {
              const newEdges = fetchMoreResult.paymentrelations.edges
              const pageInfo = fetchMoreResult.paymentrelations.pageInfo
              return {
                paymentrelations: {
                  __typename: previousResult.paymentrelations.__typename,
                  edges: [...newEdges],
                  pageInfo
                }
              }
            }
          })
        }
      }
    }
  })

export default compose(
  withRouter,
  withData
)(InvestmentProductPayments)



// WEBPACK FOOTER //
// ./src/components/investment-product-payments/index.js